import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { db, rootRef } from './firebase';
import { AUTH_LINKS_COLLECTION } from './authAccount';
import { rebuildPeerSummaryFromContribs } from './peerReportSummary';
import { availableEvalYears, defaultEvalYear, peerSummaryDocId, poolDocId } from './evalYear';

const BATCH_LIMIT = 400;

async function commitDeletes(refs: firebase.firestore.DocumentReference[]) {
  let batch = db.batch();
  let ops = 0;
  for (const ref of refs) {
    batch.delete(ref);
    ops += 1;
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = db.batch();
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
}

/**
 * HR용: 임직원 삭제 시 관련 과제·다면평가·풀·권한 링크까지 정리.
 * 익명 리포트는 브라우저에서 contrib 기준으로 재집계 (Spark / Functions 불필요).
 */
export async function deleteMemberCascade(memberId: string): Promise<{
  deletedDocs: number;
  rebuiltSummaries: number;
}> {
  const data = rootRef();
  const memberSnap = await data.collection('members').doc(memberId).get();
  const authUid = memberSnap.exists ? (memberSnap.data()?.authUid as string | undefined) : undefined;

  const [
    tasksAsOwner,
    evalsAsEvaluator,
    evalsAsColleague,
    commentsAsEvaluator,
    commentsAsColleague,
    contribsAsEvaluator,
    contribsAsColleague,
    poolsContaining,
  ] = await Promise.all([
    data.collection('tasks').where('ownerId', '==', memberId).get(),
    data.collection('peer_evals').where('evaluatorId', '==', memberId).get(),
    data.collection('peer_evals').where('colleagueId', '==', memberId).get(),
    data.collection('peer_comments').where('evaluatorId', '==', memberId).get(),
    data.collection('peer_comments').where('colleagueId', '==', memberId).get(),
    data.collection('peer_eval_contribs').where('evaluatorId', '==', memberId).get(),
    data.collection('peer_eval_contribs').where('colleagueId', '==', memberId).get(),
    data.collection('evaluator_pools').where('selectedIds', 'array-contains', memberId).get(),
  ]);

  const toDelete = new Map<string, firebase.firestore.DocumentReference>();
  const addDocs = (snap: firebase.firestore.QuerySnapshot) => {
    snap.docs.forEach((d) => toDelete.set(d.ref.path, d.ref));
  };

  addDocs(tasksAsOwner);
  addDocs(evalsAsEvaluator);
  addDocs(evalsAsColleague);
  addDocs(commentsAsEvaluator);
  addDocs(commentsAsColleague);
  addDocs(contribsAsEvaluator);
  addDocs(contribsAsColleague);

  toDelete.set(
    data.collection('peer_report_summaries').doc(memberId).path,
    data.collection('peer_report_summaries').doc(memberId)
  );
  toDelete.set(
    data.collection('evaluator_pools').doc(memberId).path,
    data.collection('evaluator_pools').doc(memberId)
  );
  availableEvalYears().forEach((year) => {
    const sId = peerSummaryDocId(year, memberId);
    const pId = poolDocId(year, memberId);
    toDelete.set(data.collection('peer_report_summaries').doc(sId).path, data.collection('peer_report_summaries').doc(sId));
    toDelete.set(data.collection('evaluator_pools').doc(pId).path, data.collection('evaluator_pools').doc(pId));
  });

  const rebuildTargets = new Map<string, { year: number; colleagueId: string }>();
  contribsAsEvaluator.docs.forEach((d) => {
    const cid = d.data().colleagueId;
    const year = Number(d.data().evalYear) || defaultEvalYear();
    if (cid && cid !== memberId) rebuildTargets.set(`${year}_${cid}`, { year, colleagueId: cid });
  });

  for (const doc of poolsContaining.docs) {
    if (doc.id === memberId || doc.id.endsWith(`_${memberId}`)) continue;
    const ids = (doc.data().selectedIds || []).filter((id: string) => id !== memberId);
    await doc.ref.set({ selectedIds: ids, updatedAt: Date.now() }, { merge: true });
  }

  await commitDeletes(Array.from(toDelete.values()));

  if (authUid) {
    await db.collection(AUTH_LINKS_COLLECTION).doc(authUid).delete().catch(() => {});
  }

  await data.collection('members').doc(memberId).delete();

  let rebuiltSummaries = 0;
  for (const t of rebuildTargets.values()) {
    try {
      await rebuildPeerSummaryFromContribs(t.year, t.colleagueId);
      rebuiltSummaries += 1;
    } catch (err) {
      console.error('[DELETE] rebuild summary failed:', t, err);
    }
  }

  return { deletedDocs: toDelete.size + 1, rebuiltSummaries };
}
